import { smsInbox, smsAccept, smsDismiss, listCategories, type SmsInboxRow, type CategoryListItem } from '../api-client';
import { showCategoryPicker } from './category-picker';
import { showToast } from '../components/toast';

function badge(row: SmsInboxRow): string {
  if (row.unknownCard) return 'Unknown card';
  return row.msgType;   // purchase | reversal | payment | unsupported
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function canAccept(row: SmsInboxRow): boolean {
  return row.status === 'pending' && row.msgType === 'purchase' && !row.unknownCard;
}

function categoryLabel(categories: CategoryListItem[], catId: string, subId: string | null): string {
  const cat = categories.find((c) => c.id === catId);
  if (!cat) return catId;
  if (subId) {
    const sub = categories.find((c) => c.id === subId);
    return sub ? `${cat.name} / ${sub.name}` : cat.name;
  }
  return cat.name;
}

export async function renderSmsInbox(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="sms-inbox"><h1>SMS Inbox</h1><div class="sms-list"></div></div>`;
  const list = root.querySelector<HTMLElement>('.sms-list')!;

  const [{ rows }, { categories }] = await Promise.all([smsInbox('pending'), listCategories()]);

  if (rows.length === 0) {
    list.innerHTML = `<p class="muted">Nothing to review.</p>`;
    return;
  }

  for (const row of rows) {
    // per-row picked category overrides the suggestion
    let pickedCat = row.suggestedCategoryId;
    let pickedSub = row.suggestedSubcategoryId;

    const el = document.createElement('div');
    el.className = 'sms-row';
    el.dataset.id = row.id;
    const amount = row.amountOriginal != null ? `${row.amountOriginal} ${row.currency ?? ''}`.trim() : '—';
    const cat = row.suggestedCategoryLabel ?? 'Pick category';
    el.innerHTML = `
      <div class="sms-main">
        <span class="sms-amount">${escapeHtml(amount)}</span>
        <span class="sms-merchant">${escapeHtml(row.merchant ?? '—')}</span>
        <span class="sms-badge sms-badge-${row.unknownCard ? 'unknown' : row.msgType}">${escapeHtml(badge(row))}</span>
      </div>
      <div class="sms-meta">
        <span>${escapeHtml(row.accountName ?? (row.cardLast4 ? `•••• ${row.cardLast4}` : '—'))}</span>
        <button class="sms-cat link" ${row.msgType === 'purchase' ? '' : 'hidden'}>${escapeHtml(cat)} ›</button>
      </div>
      <div class="sms-actions">
        <button class="sms-dismiss ghost">Dismiss</button>
        <button class="sms-accept">Accept</button>
      </div>`;

    const acceptBtn = el.querySelector<HTMLButtonElement>('.sms-accept')!;
    const catBtn = el.querySelector<HTMLButtonElement>('.sms-cat')!;

    function refreshAcceptState(): void {
      acceptBtn.disabled = !canAccept(row) || !pickedCat;
      acceptBtn.title = row.unknownCard ? 'Map this card first'
        : row.msgType !== 'purchase' ? `${row.msgType} rows can only be dismissed`
        : !pickedCat ? 'Pick a category first' : '';
    }
    refreshAcceptState();

    catBtn.onclick = () => showCategoryPicker((categoryId, subcategoryId) => {
      pickedCat = categoryId; pickedSub = subcategoryId;
      catBtn.textContent = categoryLabel(categories, categoryId, subcategoryId) + ' ›';
      refreshAcceptState();
    }, { kind: 'expense' });

    acceptBtn.onclick = async () => {
      acceptBtn.disabled = true;
      try {
        await smsAccept(row.id, { categoryId: pickedCat!, subcategoryId: pickedSub ?? null });
        el.remove();
      } catch (err) {
        acceptBtn.disabled = false;
        showToast(err instanceof Error ? err.message : 'Failed to accept — please try again', 'error');
      }
    };

    el.querySelector<HTMLButtonElement>('.sms-dismiss')!.onclick = async () => {
      try {
        await smsDismiss(row.id);
        el.remove();
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to dismiss — please try again', 'error');
      }
    };

    list.appendChild(el);
  }
}
